/**
 * CF7 Custom Select
 *
 * Replaces native <select> inside .wpcf7-form-control-wrap with a custom
 * accessible dropdown. Keeps the original <select> hidden and synced so
 * CF7 can still read its value on submit.
 */

/**
 * Build custom dropdown markup from a native <select>.
 *
 * @param {HTMLSelectElement} select
 * @return {HTMLElement} Wrapper element.
 */
const buildDropdown = ( select ) => {

	const options = Array.from( select.options );

	const firstIsLabel = options.length > 0 && options[ 0 ].value === "";
	const labelOption = firstIsLabel ? options[ 0 ] : null;
	const selectableOptions = firstIsLabel ? options.slice( 1 ) : options;

	const selected = ! firstIsLabel && select.selectedIndex >= 0
		? select.options[ select.selectedIndex ]
		: null;

	const wrapper = document.createElement( "div" );
	wrapper.className = "cf7-select js-cf7-select";

	const trigger = document.createElement( "button" );
	trigger.type = "button";
	trigger.className = "cf7-select__trigger js-cf7-select-trigger";
	trigger.setAttribute( "aria-haspopup", "listbox" );
	trigger.setAttribute( "aria-expanded", "false" );

	const label = document.createElement( "span" );
	label.className = "cf7-select__label js-cf7-select-label";

	if ( labelOption ) {

		label.textContent = labelOption.text;
		label.dataset.placeholder = labelOption.text;

	} else {

		label.textContent = selected ? selected.text : "";

	}

	const arrow = document.createElement( "span" );
	arrow.className = "cf7-select__arrow";
	arrow.setAttribute( "aria-hidden", "true" );

	trigger.appendChild( label );
	trigger.appendChild( arrow );

	const dropdown = document.createElement( "div" );
	dropdown.className = "cf7-select__dropdown";
	dropdown.setAttribute( "role", "listbox" );

	selectableOptions.forEach( ( opt ) => {

		const item = document.createElement( "div" );
		item.className = "cf7-select__option js-cf7-select-option";
		item.setAttribute( "role", "option" );
		item.setAttribute( "tabindex", "-1" );
		item.dataset.value = opt.value;

		if ( opt.selected ) {

			item.classList.add( "is-selected" );
			item.setAttribute( "aria-selected", "true" );

		} else {

			item.setAttribute( "aria-selected", "false" );

		}

		const text = document.createElement( "span" );
		text.textContent = opt.text;

		item.appendChild( text );
		dropdown.appendChild( item );

	} );

	wrapper.appendChild( trigger );
	wrapper.appendChild( dropdown );

	return wrapper;

};

/**
 * Initialise custom CF7 selects.
 *
 * @return {void}
 */
const initCf7Select = () => {

	const wraps = document.querySelectorAll( ".wpcf7-form-control-wrap:has(select)" );

	wraps.forEach( ( wrap ) => {

		const select = wrap.querySelector( "select" );

		if ( ! select || wrap.querySelector( ".js-cf7-select" ) ) {

			return;

		}

		// Hide native select but keep it in DOM for CF7
		select.style.display = "none";
		select.setAttribute( "aria-hidden", "true" );
		select.setAttribute( "tabindex", "-1" );

		const wrapper = buildDropdown( select );
		wrap.insertBefore( wrapper, select );

		const trigger = wrapper.querySelector( ".js-cf7-select-trigger" );
		const labelEl = wrapper.querySelector( ".js-cf7-select-label" );
		const options = wrapper.querySelectorAll( ".js-cf7-select-option" );

		if ( labelEl.dataset.placeholder ) {

			trigger.classList.add( "is-placeholder" );

		}

		/**
		 * Returns options as an array.
		 *
		 * @return {HTMLElement[]} Array of option elements.
		 */
		const getList = () => Array.from( options );

		let justClosed = false;

		/**
		 * Close the dropdown.
		 *
		 * @return {void}
		 */
		const close = () => {

			wrapper.classList.remove( "is-open" );
			trigger.setAttribute( "aria-expanded", "false" );

			justClosed = true;
			setTimeout( () => {

				justClosed = false;

			}, 150 );

		};

		/**
		 * Open the dropdown and focus the selected (or first) option.
		 *
		 * @return {void}
		 */
		const open = () => {

			wrapper.classList.add( "is-open" );
			trigger.setAttribute( "aria-expanded", "true" );

			const list = getList();
			const selectedIndex = list.findIndex( ( o ) => o.classList.contains( "is-selected" ) );
			const focusIndex = selectedIndex !== -1 ? selectedIndex : 0;
			list[ focusIndex ].focus();

		};

		trigger.addEventListener( "click", () => {

			if ( justClosed ) {

				return;

			}

			if ( wrapper.classList.contains( "is-open" ) ) {

				close();

			} else {

				open();

			}

		} );

		document.addEventListener( "click", ( e ) => {

			if ( ! wrapper.contains( e.target ) ) {

				close();

			}

		} );

		wrapper.addEventListener( "focusout", ( e ) => {

			if ( ! wrapper.contains( e.relatedTarget ) ) {

				close();

			}

		} );

		wrapper.addEventListener( "keydown", ( e ) => {

			const isOpen = wrapper.classList.contains( "is-open" );

			if ( e.key === "Escape" && isOpen ) {

				close();
				trigger.focus();
				return;

			}

			if ( ( e.key === "ArrowDown" || e.key === "ArrowUp" ) && isOpen ) {

				e.preventDefault();

				const list = getList();
				const currentIndex = list.indexOf( wrapper.ownerDocument.activeElement );
				const total = list.length;
				let nextIndex;

				if ( e.key === "ArrowDown" ) {

					nextIndex = currentIndex === -1 ? 0 : ( currentIndex + 1 ) % total;

				} else {

					nextIndex = currentIndex === -1 ? total - 1 : ( currentIndex - 1 + total ) % total;

				}

				list[ nextIndex ].focus();

			}

			if ( ( e.key === "Enter" || e.key === " " ) && ! isOpen ) {

				e.preventDefault();
				open();

			}

		} );

		options.forEach( ( option ) => {

			option.addEventListener( "mousedown", ( e ) => {

				e.preventDefault();

			} );

			option.addEventListener( "click", ( e ) => {

				e.stopPropagation();

				const value = option.dataset.value;
				const text = option.querySelector( "span" ).textContent;

				getList().forEach( ( opt ) => {

					opt.classList.remove( "is-selected" );
					opt.setAttribute( "aria-selected", "false" );

				} );

				option.classList.add( "is-selected" );
				option.setAttribute( "aria-selected", "true" );
				labelEl.textContent = text;
				trigger.classList.remove( "is-placeholder" );

				// Sync native select
				select.value = value;
				select.dispatchEvent( new Event( "change", { bubbles: true } ) );

				// Clear validation error when user picks an option
				trigger.classList.remove( "is-invalid" );

				close();
				trigger.focus();

			} );

			option.addEventListener( "keydown", ( e ) => {

				if ( e.key === "Enter" || e.key === " " ) {

					e.preventDefault();
					option.click();

				}

			} );

		} );

		// Mirror CF7 validation state (wpcf7-not-valid) to custom trigger
		const observer = new MutationObserver( () => {

			const isInvalid = select.classList.contains( "wpcf7-not-valid" );
			trigger.classList.toggle( "is-invalid", isInvalid );

		} );

		observer.observe( select, { attributes: true, attributeFilter: [ "class" ] } );

	} );

};

document.addEventListener( "DOMContentLoaded", initCf7Select );

// Re-init after CF7 AJAX
document.addEventListener( "wpcf7mailsent", initCf7Select );
